// EventMapKit — the shared shot/pass event engine, ported ONCE from the prototype's
// window.EventMapKit (js/renderers-shotmap.js). Ben's rule: the two shared engines
// (StatsFilterKit, EventMapKit) are shared modules, never copied per renderer. Heat Map,
// Shot Map, Pass Map, Passing Profile (04l) and Zone Finder (04m) all build on it.
//
// Scope of THIS module (04l-a): the PURE engine — pitch geometry + zoom math, the
// match/opposition/game-state labels, the event-filter engine (exclude + cross-filter +
// self-healing prune), SHOT/PASS filter defs, and the colour modes. The DOM UI (filter bar
// + popovers, pitch drawing, area-drag gesture, sub-controls) is React in 04l-b onward.
// The cross-widget link lives next door in ./event-link.ts.
//
// Data-injection deviation (same as StatsFilterKit / matches-view): the legacy reads
// window.CatalystData (matchResults / matchGoals / players) inside the Opposition /
// Game State / Recipient helpers. Here that data arrives as an EventContext, so the
// engine stays pure and the Track-2 cutover stays behind the hooks. The filter defs that
// need it are therefore factories (`shotFilters(ctx)` / `passFilters(ctx)`).
//
// The legacy window.EventMapKit keeps running for the not-yet-converted renderers (Zone
// Finder) until the window.* globals retire in Phase 05 — both must stay in lockstep.

import type { ProtoEvent, ProtoEventCompetition, ProtoEventSeason, ProtoFixture, ProtoMatchGoal, ProtoShotPassDetail } from "@catbento/shared";
import { getPlayerShortName } from "../data/player-utils.js";
import { humanizeSeasonCode } from "../modules/league-table-view.js";
import { seasonSortKey } from "../data/league.js";

// Identical logic already ported (league-table-view / data/league) — re-exported so kit
// consumers have one import, not a third copy.
export { humanizeSeasonCode, seasonSortKey };

// --- Pitch ------------------------------------------------------------------------
// The captured coordinates cap at exactly 114×74 in every competition (a systematic
// upstream clamp, not StatsBomb's textbook 120×80), so the markings are drawn to the
// real extent — otherwise corners sit inside the six-yard box.
export const PITCH_L = 114;
export const PITCH_W = 74;

/** Catalyst's export is already attack-direction-normalised (period 2 isn't mirrored),
 *  so no per-period flip. Kept as the one seam for it. */
export function normalize(x: number, y: number): [number, number] {
  return [x, y];
}

/** freeze_frame locations + shot.end_location are in StatsBomb's TRUE 120×80 space, not
 *  the rescaled 114×74 every other x/y uses — convert before plotting (else the keeper
 *  sits permanently off-canvas past x=114). */
export function trueScaleToPitch(x: number, y: number): [number, number] {
  return [x * (PITCH_L / 120), y * (PITCH_W / 80)];
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** One static pitch marking, in pitch units — rendered as JSX by the pitch component. */
export type PitchMarking =
  | { tag: "rect"; className: "pitch-line"; x: number; y: number; width: number; height: number }
  | { tag: "line"; className: "pitch-line"; x1: number; y1: number; x2: number; y2: number }
  | { tag: "circle"; className: "pitch-line" | "pitch-spot"; cx: number; cy: number; r: number }
  | { tag: "path"; className: "pitch-line"; d: string };

/** The legacy pitchMarkupSvg() as data, same elements in the same order (paint order
 *  matters for identical output). Lines/boxes/arcs are fill="none" in the renderer. */
export function pitchMarkings(): PitchMarking[] {
  const boxY1 = PITCH_W / 2 - 22, boxY2 = PITCH_W / 2 + 22;
  const sixY1 = PITCH_W / 2 - 10, sixY2 = PITCH_W / 2 + 10;
  return [
    { tag: "rect", className: "pitch-line", x: 0, y: 0, width: PITCH_L, height: PITCH_W },
    { tag: "line", className: "pitch-line", x1: PITCH_L / 2, y1: 0, x2: PITCH_L / 2, y2: PITCH_W },
    { tag: "circle", className: "pitch-line", cx: PITCH_L / 2, cy: PITCH_W / 2, r: 9.15 },
    { tag: "circle", className: "pitch-spot", cx: PITCH_L / 2, cy: PITCH_W / 2, r: 0.4 },
    { tag: "rect", className: "pitch-line", x: 0, y: boxY1, width: 18, height: boxY2 - boxY1 },
    { tag: "rect", className: "pitch-line", x: 0, y: sixY1, width: 6, height: sixY2 - sixY1 },
    { tag: "circle", className: "pitch-spot", cx: 12, cy: PITCH_W / 2, r: 0.4 },
    { tag: "path", className: "pitch-line", d: `M 18 ${PITCH_W / 2 - 8} A 9.15 9.15 0 0 1 18 ${PITCH_W / 2 + 8}` },
    { tag: "rect", className: "pitch-line", x: PITCH_L - 18, y: boxY1, width: 18, height: boxY2 - boxY1 },
    { tag: "rect", className: "pitch-line", x: PITCH_L - 6, y: sixY1, width: 6, height: sixY2 - sixY1 },
    { tag: "circle", className: "pitch-spot", cx: PITCH_L - 12, cy: PITCH_W / 2, r: 0.4 },
    {
      tag: "path",
      className: "pitch-line",
      d: `M ${PITCH_L - 18} ${PITCH_W / 2 - 8} A 9.15 9.15 0 0 0 ${PITCH_L - 18} ${PITCH_W / 2 + 8}`,
    },
  ];
}

// --- Rotation + drag-to-zoom (Shot Map / Pass Map) ---------------------------------

/** A selected rectangle, in real (unrotated) pitch units. */
export interface EventArea {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** The rotated pitch (attack toward the top) as a pure rotation+translation. The viewBox
 *  is sized to the selection's own span, so there's no scale term — a zoom never
 *  stretches circles into ellipses. With no area this reduces to matrix(0,-1,1,0,0,114). */
export function pitchRotateMatrix(area: EventArea | null): string {
  const a = area ?? { x1: 0, y1: 0, x2: PITCH_L, y2: PITCH_W };
  const postH = a.x2 - a.x1;
  return `matrix(0,-1,1,0,${-a.y1},${postH + a.x1})`;
}

/** Everything applyPitchZoom sets on the svg. The rotation swaps axes: the area's real
 *  y-span is the new width, its x-span the new height. aspectRatio '' = let the stylesheet's
 *  static 74:114 rule win again when not zoomed. */
export function pitchZoomView(area: EventArea | null): {
  viewBox: string;
  aspectRatio: string;
  transform: string;
  zoomed: boolean;
} {
  const a = area ?? { x1: 0, y1: 0, x2: PITCH_L, y2: PITCH_W };
  const postW = a.y2 - a.y1;
  const postH = a.x2 - a.x1;
  return {
    viewBox: `0 0 ${postW} ${postH}`,
    aspectRatio: area ? `${postW} / ${postH}` : "",
    transform: pitchRotateMatrix(area),
    zoomed: !!area,
  };
}

/** An event's ORIGIN inside the area (shots and passes share the x/y fields). */
export function eventInArea(event: { x: number; y: number }, area: EventArea | null): boolean {
  return !area || (event.x >= area.x1 && event.x <= area.x2 && event.y >= area.y1 && event.y <= area.y2);
}

/** Screen px a pointerdown must travel before it's an area drag (below it, click/hover
 *  behave unchanged); pitch units below which a finished rectangle is jitter. */
export const AREA_DRAG_THRESHOLD_PX = 6;
export const AREA_MIN_SIZE = 3;

/** Clamp a local point to the pitch — the live drag preview never implies more pitch than exists. */
export function clampToPitch(p: { x: number; y: number }): { x: number; y: number } {
  return { x: clamp(p.x, 0, PITCH_L), y: clamp(p.y, 0, PITCH_W) };
}

/** The committed selection from the drag's two local corners, or null if too small. */
export function areaFromDrag(p1: { x: number; y: number }, p2: { x: number; y: number }): EventArea | null {
  const x1 = clamp(Math.min(p1.x, p2.x), 0, PITCH_L);
  const x2 = clamp(Math.max(p1.x, p2.x), 0, PITCH_L);
  const y1 = clamp(Math.min(p1.y, p2.y), 0, PITCH_W);
  const y2 = clamp(Math.max(p1.y, p2.y), 0, PITCH_W);
  return x2 - x1 >= AREA_MIN_SIZE && y2 - y1 >= AREA_MIN_SIZE ? { x1, y1, x2, y2 } : null;
}

/** Has a press moved far enough (screen px) to become an area drag? */
export function isAreaDrag(dx: number, dy: number): boolean {
  return Math.hypot(dx, dy) >= AREA_DRAG_THRESHOLD_PX;
}

/** The live dashed overlay's box, in px relative to the pitch wrap, from the two (already
 *  clamped, back-to-screen) corners — so it stops at the pitch edge however far the cursor goes. */
export function areaOverlayBox(
  a: { x: number; y: number },
  b: { x: number; y: number },
  wrap: { left: number; top: number },
): { left: number; top: number; width: number; height: number } {
  return {
    left: Math.min(a.x, b.x) - wrap.left,
    top: Math.min(a.y, b.y) - wrap.top,
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
  };
}

/** Point → segment distance (the magnetic pass/wheel hover). */
export function distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1, dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  let t = lenSq === 0 ? 0 : ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/** A magnetic hover's radii (in the chart's own units): acquire within `select`, keep the
 *  current line within `sticky` unless another is closer than `switchMargin` × its distance. */
export interface MagneticRadii {
  select: number;
  sticky: number;
  switchMargin: number;
}

/** The next hovered line given the current one — the nearest by point-to-segment distance,
 *  "magnetic" so two crossing lines don't flicker on every pixel. Shared by Pass Map and
 *  Passing Profile (the legacy kept two copies with different radii). First-nearest wins a
 *  tie (the legacy's `<`). */
export function nextMagneticSegment<T extends { x1: number; y1: number; x2: number; y2: number }>(
  p: { x: number; y: number },
  lines: T[],
  current: T | null,
  r: MagneticRadii,
): T | null {
  const dist = (s: T) => distToSegment(p.x, p.y, s.x1, s.y1, s.x2, s.y2);
  let best: T | null = null;
  let bestDist = Infinity;
  for (const ld of lines) {
    const d = dist(ld);
    if (d < bestDist) {
      bestDist = d;
      best = ld;
    }
  }
  if (current) {
    const curDist = dist(current);
    const stolenByCloser = best !== current && bestDist < curDist * r.switchMargin;
    if (curDist <= r.sticky && !stolenByCloser) return current;
  }
  return best && bestDist <= r.select ? best : null;
}

// --- Competition / season state -----------------------------------------------------

export interface ResolvedEventState {
  compId: string;
  comp: ProtoEventCompetition;
  seasonCodes: string[];
  seasonCode: string;
  season: ProtoEventSeason;
}

/** The module's stored competition/season (`_eventMapCompetitionId` / `_eventMapSeason`)
 *  resolved against THIS player's event data: an unknown/absent id falls back to the first
 *  competition, an unknown season to its newest. null = no event data. Pure — the caller
 *  writes the resolved ids back to the module (the legacy did it inside). */
export function resolveEventState(
  stored: { compId?: string | null; seasonCode?: string | null },
  spd: ProtoShotPassDetail | null,
): ResolvedEventState | null {
  if (!spd) return null;
  const compIds = Object.keys(spd.byCompetition);
  if (!compIds.length) return null;
  const compId = stored.compId && compIds.includes(stored.compId) ? stored.compId : compIds[0]!;
  const comp = spd.byCompetition[compId]!;
  const seasonCodes = Object.keys(comp.seasons).sort((a, b) => seasonSortKey(b) - seasonSortKey(a));
  const seasonCode = stored.seasonCode && seasonCodes.includes(stored.seasonCode) ? stored.seasonCode : seasonCodes[0]!;
  return { compId, comp, seasonCodes, seasonCode, season: comp.seasons[seasonCode]! };
}

// --- StatsBomb attribute narrowing ----------------------------------------------------
// ProtoEvent keeps raw_event_attributes loose; these are the fields the kit + its
// renderers read. Unguarded where the legacy is unguarded (fail fast), optional where the
// legacy checks presence (e.g. pass.type is absent on ordinary open-play passes).

interface Named {
  /** StatsBomb's id (e.g. pass outcome 76 = offside, pass type 61–67 = set pieces). */
  id?: number;
  name: string;
}
export interface SbShot {
  outcome: Named;
  body_part: Named;
  type: Named;
  technique?: Named;
  statsbomb_xg?: number;
  key_pass_id?: string;
  deflected?: boolean;
  one_on_one?: boolean;
  first_time?: boolean;
  follows_dribble?: boolean;
  saved_off_target?: boolean;
  end_location?: number[];
  freeze_frame?: unknown[];
  [key: string]: unknown;
}
export interface SbPass {
  type?: Named;
  outcome?: Named;
  body_part?: Named;
  recipient?: { id: number; name: string } | null;
  height?: Named;
  goal_assist?: boolean;
  shot_assist?: boolean;
  xclaim?: number;
  miscommunication?: boolean;
  pass_success_probability?: number;
  length?: number;
  [key: string]: unknown;
}
export interface SbAttrs {
  team: Named;
  position?: Named;
  play_pattern?: Named;
  under_pressure?: boolean;
  obv_total_net?: number;
  shot?: SbShot;
  pass?: SbPass;
  [key: string]: unknown;
}
export interface SbEnriched {
  from_cross?: boolean;
  from_throughball?: boolean;
  on_target?: boolean;
  [key: string]: unknown;
}

export const sbAttrs = (e: ProtoEvent) => e.raw_event_attributes as SbAttrs;
export const shotOf = (e: ProtoEvent) => sbAttrs(e).shot as SbShot;
export const passOf = (e: ProtoEvent) => sbAttrs(e).pass as SbPass;
export const enrichedOf = (e: ProtoEvent) => e.enriched_event_attributes as SbEnriched | undefined;

/** pass.type exists only for special kinds (Throw-in, Corner, …); absent = open play. */
export function passTypeName(pass: ProtoEvent): string {
  const type = passOf(pass).type;
  return type ? type.name : "Open Play";
}

/** StatsBomb records an outcome only for a FAILED pass. */
export function passCompleted(pass: ProtoEvent): boolean {
  return !passOf(pass).outcome;
}

/** body_part is absent on throw-ins (a hands action) — undefined, never a fake "N/A". */
export function passBodyPart(pass: ProtoEvent): string | undefined {
  const bp = passOf(pass).body_part;
  return bp ? bp.name : undefined;
}

// --- Context-dependent labels -----------------------------------------------------------

/** The snapshot data the Opposition / Game State / Recipient helpers need (the legacy
 *  read these off window.CatalystData). */
export interface EventContext {
  matchResults: Record<string, ProtoFixture[]>;
  /** Only each goal's side + minute (Game State) — so the live adapter, whose goals have no
   *  second / shot type / body part, can build it too. */
  matchGoals: Record<string, { goals: Pick<ProtoMatchGoal, "team" | "minute">[] }>;
  /** Players keyed by String(id) — the recipient lookup. */
  playersById: Map<string, { name?: string | null }>;
}

/** Index players by id (the legacy's lazily-built _recipientCache). */
export function buildPlayersById(players: Record<string, { id?: unknown; name?: string | null }>): Map<string, { name?: string | null }> {
  const byId = new Map<string, { name?: string | null }>();
  for (const pl of Object.values(players)) byId.set(String(pl.id), pl);
  return byId;
}

export function formatMatchDate(dateStr: string): string {
  return String(dateStr).slice(0, 10);
}

/** MM/DD/YY — only the Opposition chip's label uses it. */
export function formatShortDate(dateStr: string): string {
  const parts = String(dateStr).slice(0, 10).split("-");
  return `${parts[1]}/${parts[2]}/${parts[0]!.slice(2)}`;
}

/** 1/2 = halves, 3/4 = real extra time (a cup match went to ET). */
export function periodLabel(period: number): string {
  return ({ 1: "1st Half", 2: "2nd Half", 3: "1st ET", 4: "2nd ET" } as Record<number, string>)[period] || `Period ${period}`;
}

export interface EventOpposition {
  opponent: string;
  isHome: boolean;
  homeScore: number;
  awayScore: number;
}

/** Match by numeric match_id across every matchResults competition (the event data's
 *  competition names don't string-match its keys). null = not captured (e.g. UWCL) — an
 *  honest gap, never a fabricated opponent. */
export function resolveOpposition(ctx: EventContext, event: ProtoEvent): EventOpposition | null {
  for (const compKey of Object.keys(ctx.matchResults)) {
    const found = ctx.matchResults[compKey]!.find((m) => m.matchId === event.match_id);
    if (found) {
      const myTeam = sbAttrs(event).team.name;
      const isHome = myTeam === found.homeTeamName;
      return {
        opponent: isHome ? found.awayTeamName : found.homeTeamName,
        isHome,
        homeScore: found.homeScore,
        awayScore: found.awayScore,
      };
    }
  }
  return null;
}

/** "Opponent (H) MM/DD/YY" — the Opposition chip needs the date to tell two meetings apart. */
export function matchLabel(ctx: EventContext, event: ProtoEvent): string {
  const shortDate = formatShortDate(event.match_date);
  const opp = resolveOpposition(ctx, event);
  return opp ? `${opp.opponent} (${opp.isHome ? "H" : "A"}) ${shortDate}` : shortDate;
}

/** Date-free variant for hover tooltips (they already show the minute). */
export function oppositionLabel(ctx: EventContext, event: ProtoEvent): string {
  const opp = resolveOpposition(ctx, event);
  return opp ? `${opp.opponent} (${opp.isHome ? "H" : "A"})` : "";
}

/** The final score, always home-first (literal scoreline), with W/L as a CSS class —
 *  never a trailing letter (an away "2-0 W" read like a home score). "—" when uncaptured. */
export function matchResultDisplay(
  ctx: EventContext,
  event: ProtoEvent,
): { opposition: EventOpposition | null; text: string; className: string } {
  const opposition = resolveOpposition(ctx, event);
  if (!opposition || typeof opposition.homeScore !== "number" || typeof opposition.awayScore !== "number") {
    return { opposition, text: "—", className: "" };
  }
  const myScore = opposition.isHome ? opposition.homeScore : opposition.awayScore;
  const oppScore = opposition.isHome ? opposition.awayScore : opposition.homeScore;
  const text = `${opposition.homeScore}-${opposition.awayScore}`;
  const className = myScore > oppScore ? "event-result-win" : myScore < oppScore ? "event-result-loss" : "";
  return { opposition, text, className };
}

/** Winning/Drawing/Losing at the moment of the event, from the reconstructed goal
 *  timeline (event.minute is the continuous match clock, same as goals[].minute).
 *  undefined outside matchGoals coverage — the option list then drops it, so the filter
 *  never fabricates a state. (Known limit: own goals aren't in the reconstruction.) */
export function resolveGameState(ctx: EventContext, event: ProtoEvent): string | undefined {
  const match = ctx.matchGoals[String(event.match_id)];
  if (!match) return undefined;
  const opp = resolveOpposition(ctx, event);
  if (!opp) return undefined;
  let myScore = 0;
  let oppScore = 0;
  for (const g of match.goals) {
    if (g.minute > event.minute) continue;
    if ((g.team === "home") === opp.isHome) myScore++;
    else oppScore++;
  }
  if (myScore > oppScore) return "Winning";
  if (myScore < oppScore) return "Losing";
  return "Drawing";
}

/** Resolve the recipient through the roster by stable id — StatsBomb's raw name string is
 *  inconsistent per event ("Alessia Russo" vs "Alessia Mia Teresa Russo") and would split
 *  one teammate across two chip options. */
export function recipientShortName(ctx: EventContext, pass: ProtoEvent): string {
  const r = passOf(pass).recipient;
  if (!r) return "Unknown";
  const pl = ctx.playersById.get(String(r.id));
  return pl ? getPlayerShortName(pl) : r.name;
}

// --- Event-filter engine ---------------------------------------------------------------
// Separate from StatsFilterKit on purpose (the legacy keeps them apart): that engine is
// hardcoded to player bio fields; this one filters shot/pass rows. Same exclude shape.

export interface ExcludeValue {
  exclude: true;
  value: string;
}
export type CategoricalActive = string | ExcludeValue;
export interface RangeActive {
  min: number;
  max: number;
}
/** A module's own mutable `_eventFilters` / `_heatmapFilters` object. */
export type EventActive = Record<string, CategoricalActive | RangeActive>;

// Generic over the event shape: shot/pass rows by default, but Zone Finder (04m) runs the
// same engine over StatsBomb open-data events (a flat {outcome, bodyPart, period, …} shape).
// The engine only ever reads an event through its def's getValue, so any shape works.
export interface CategoricalFilterDef<E = ProtoEvent> {
  key: string;
  label: string;
  type?: undefined;
  getValue: (event: E) => string | undefined;
}
export interface RangeFilterDef<E = ProtoEvent> {
  key: string;
  label: string;
  type: "range";
  domain: { min: number; max: number };
  precision?: number;
  unit?: string;
  format?: (v: number) => string;
  getValue: (event: E) => number | null;
}
export type EventFilterDef<E = ProtoEvent> = CategoricalFilterDef<E> | RangeFilterDef<E>;

export function excludeValue(value: string): ExcludeValue {
  return { exclude: true, value };
}
export function isExcludeValue(v: unknown): v is ExcludeValue {
  return v !== null && typeof v === "object" && (v as { exclude?: unknown }).exclude === true;
}
export function categoricalValueOf(v: CategoricalActive): string {
  return isExcludeValue(v) ? v.value : v;
}
export function matchesCategorical(actual: string | undefined, active: CategoricalActive | undefined): boolean {
  if (active === undefined) return true;
  if (isExcludeValue(active)) return actual !== active.value;
  return actual === active;
}
export function formatCategoricalValue(v: CategoricalActive): string {
  return isExcludeValue(v) ? `Not ${v.value}` : v;
}

/** Continuous match clock (period 2 continues from 45, ET from 90) — shared by both event types. */
export const MINUTE_FILTER: RangeFilterDef = {
  key: "minute",
  label: "Time",
  type: "range",
  unit: "′",
  domain: { min: 0, max: 120 },
  getValue: (event) => event.minute,
};

const yesNo = (v: unknown) => (v ? "Yes" : "No");
const signed2 = (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}`;
const numOrNull = (v: unknown) => (typeof v === "number" ? v : null);

/** Shot Map's filters, VERBATIM order/labels/domains (order = the add-menu order). */
export function shotFilters(ctx: EventContext): EventFilterDef[] {
  return [
    { key: "outcome", label: "Outcome", getValue: (s) => shotOf(s).outcome.name },
    { key: "bodyPart", label: "Body Part", getValue: (s) => shotOf(s).body_part.name },
    { key: "type", label: "Type", getValue: (s) => shotOf(s).type.name },
    { key: "technique", label: "Technique", getValue: (s) => (shotOf(s).technique ? shotOf(s).technique!.name : undefined) },
    { key: "position", label: "Position", getValue: (s) => (sbAttrs(s).position ? sbAttrs(s).position!.name : undefined) },
    { key: "playPattern", label: "Play Pattern", getValue: (s) => (sbAttrs(s).play_pattern ? sbAttrs(s).play_pattern!.name : undefined) },
    { key: "assisted", label: "Assisted", getValue: (s) => (shotOf(s).key_pass_id ? "Assisted" : "Unassisted") },
    // No fromKeyPass: it's 100% identical to Assisted. fromCross/fromThroughball are real
    // sub-cases Assisted alone doesn't carry.
    { key: "fromCross", label: "From Cross", getValue: (s) => yesNo(enrichedOf(s)?.from_cross) },
    { key: "fromThroughball", label: "From Through Ball", getValue: (s) => yesNo(enrichedOf(s)?.from_throughball) },
    { key: "underPressure", label: "Under Pressure", getValue: (s) => yesNo(sbAttrs(s).under_pressure) },
    { key: "deflected", label: "Deflected", getValue: (s) => yesNo(shotOf(s).deflected) },
    { key: "oneOnOne", label: "One on One", getValue: (s) => yesNo(shotOf(s).one_on_one) },
    { key: "firstTime", label: "First Time", getValue: (s) => yesNo(shotOf(s).first_time) },
    { key: "followsDribble", label: "Follows Dribble", getValue: (s) => yesNo(shotOf(s).follows_dribble) },
    { key: "savedOffTarget", label: "Saved Off Target", getValue: (s) => yesNo(shotOf(s).saved_off_target) },
    { key: "half", label: "Half", getValue: (s) => periodLabel(s.period) },
    { key: "opposition", label: "Opposition", getValue: (s) => matchLabel(ctx, s) },
    { key: "gameState", label: "Game State", getValue: (s) => resolveGameState(ctx, s) },
    // xG's domain is its theoretical bound; OBV's is padded around the real shot range
    // (-0.763..+0.729) — a different population from passes'.
    { key: "xg", label: "xG", type: "range", precision: 2, domain: { min: 0, max: 1 }, getValue: (s) => numOrNull(shotOf(s).statsbomb_xg) },
    {
      key: "obv",
      label: "OBV (Total)",
      type: "range",
      precision: 2,
      domain: { min: -0.8, max: 0.8 },
      format: signed2,
      getValue: (s) => numOrNull(sbAttrs(s).obv_total_net),
    },
    MINUTE_FILTER,
  ];
}

/** Pass Map's filters (also Passing Profile's), VERBATIM order/labels/domains. */
export function passFilters(ctx: EventContext): EventFilterDef[] {
  return [
    { key: "type", label: "Type", getValue: passTypeName },
    { key: "completion", label: "Outcome", getValue: (p) => (passCompleted(p) ? "Complete" : "Incomplete") },
    { key: "bodyPart", label: "Body Part", getValue: passBodyPart },
    { key: "recipient", label: "Recipient", getValue: (p) => recipientShortName(ctx, p) },
    { key: "half", label: "Half", getValue: (p) => periodLabel(p.period) },
    { key: "opposition", label: "Opposition", getValue: (p) => matchLabel(ctx, p) },
    { key: "gameState", label: "Game State", getValue: (p) => resolveGameState(ctx, p) },
    { key: "playPattern", label: "Play Pattern", getValue: (p) => (sbAttrs(p).play_pattern ? sbAttrs(p).play_pattern!.name : undefined) },
    { key: "height", label: "Height", getValue: (p) => (passOf(p).height ? passOf(p).height!.name : undefined) },
    { key: "underPressure", label: "Under Pressure", getValue: (p) => yesNo(sbAttrs(p).under_pressure) },
    {
      key: "keyPass",
      label: "Key Pass",
      getValue: (p) => (passOf(p).goal_assist ? "Goal Assist" : passOf(p).shot_assist ? "Key Pass" : "No"),
    },
    { key: "xclaim", label: "GK Claim Risk", type: "range", precision: 2, domain: { min: 0, max: 0.8 }, getValue: (p) => numOrNull(passOf(p).xclaim) },
    { key: "miscommunication", label: "Miscommunication", getValue: (p) => yesNo(passOf(p).miscommunication) },
    {
      key: "successProbability",
      label: "Success Probability",
      type: "range",
      unit: "%",
      domain: { min: 0, max: 100 },
      getValue: (p) => {
        const v = passOf(p).pass_success_probability;
        return typeof v === "number" ? v * 100 : null;
      },
    },
    {
      key: "obv",
      label: "OBV (Total)",
      type: "range",
      precision: 2,
      domain: { min: -0.25, max: 0.75 },
      format: signed2,
      getValue: (p) => numOrNull(sbAttrs(p).obv_total_net),
    },
    { key: "passLength", label: "Length", type: "range", unit: " yds", domain: { min: 0, max: 100 }, getValue: (p) => numOrNull(passOf(p).length) },
    MINUTE_FILTER,
  ];
}

/** A categorical filter's options: distinct truthy values, sorted. With active + defs it's
 *  CROSS-FILTERED against every OTHER active filter (skipping its own, so a chosen value
 *  can still be changed). */
export function eventFilterOptions<E = ProtoEvent>(
  events: E[],
  def: CategoricalFilterDef<E>,
  active?: EventActive,
  defs?: EventFilterDef<E>[],
): string[] {
  const scoped = active && defs ? events.filter((e) => matchesEventFilters(e, active, defs, def.key)) : events;
  return Array.from(new Set(scoped.map(def.getValue).filter(Boolean) as string[])).sort();
}

/** Every active filter matches (exceptKey skips one). A range needs a real number inside it. */
export function matchesEventFilters<E = ProtoEvent>(
  event: E,
  active: EventActive,
  defs: EventFilterDef<E>[],
  exceptKey?: string,
): boolean {
  return Object.keys(active).every((key) => {
    if (key === exceptKey) return true;
    const def = defs.find((f) => f.key === key);
    if (!def) return true;
    if (def.type === "range") {
      const v = def.getValue(event);
      const r = active[key] as RangeActive;
      return typeof v === "number" && v >= r.min && v <= r.max;
    }
    return matchesCategorical(def.getValue(event), active[key] as CategoricalActive);
  });
}

/** Self-heal IN PLACE (the module's own object): drop unknown keys + categorical values no
 *  longer present in this event set (e.g. after a season switch); keep ones that still
 *  apply. Ranges are absolute domains, never stale. Checked against the plain, un-cross-
 *  filtered set on purpose — cross-filtering here would be an order-dependent cascade. */
export function pruneStaleEventFilters<E = ProtoEvent>(active: EventActive, events: E[], defs: EventFilterDef<E>[]): void {
  for (const key of Object.keys(active)) {
    const def = defs.find((f) => f.key === key);
    if (!def) {
      delete active[key];
      continue;
    }
    if (def.type === "range") continue;
    if (!eventFilterOptions(events, def).includes(categoricalValueOf(active[key] as CategoricalActive))) delete active[key];
  }
}

function rangeFmt<E>(def: RangeFilterDef<E>): (v: number) => string {
  return def.format ?? ((v) => `${v.toFixed(def.precision || 0)}${def.unit || ""}`);
}

/** A domain end label on the range picker. */
export function formatEventRangeValue<E>(def: RangeFilterDef<E>, v: number): string {
  return rangeFmt(def)(v);
}

/** The chip label + picker readout for a range. */
export function formatEventRange<E>(def: RangeFilterDef<E>, range: RangeActive): string {
  const fmt = rangeFmt(def);
  return range.min === range.max ? `Exactly ${fmt(range.min)}` : `${fmt(range.min)} to ${fmt(range.max)}`;
}

/** Range picker: value → handle position (% of the track). */
export function rangeValueToPct<E>(def: RangeFilterDef<E>, v: number): number {
  return ((v - def.domain.min) / (def.domain.max - def.domain.min)) * 100;
}

/** Range picker: pointer offset → value, clamped to the domain and rounded to the precision. */
export function rangePxToValue<E>(def: RangeFilterDef<E>, px: number, trackWidth: number): number {
  const pct = Math.max(0, Math.min(1, px / trackWidth));
  const factor = Math.pow(10, def.precision || 0);
  return Math.round((def.domain.min + pct * (def.domain.max - def.domain.min)) * factor) / factor;
}

/** The value a freshly-added filter starts at: a range spans its whole domain; a
 *  categorical takes the first cross-filtered option (none → not added). */
export function newFilterValue<E = ProtoEvent>(
  def: EventFilterDef<E>,
  events: E[],
  active: EventActive,
  defs: EventFilterDef<E>[],
): CategoricalActive | RangeActive | undefined {
  if (def.type === "range") return { min: def.domain.min, max: def.domain.max };
  const opts = eventFilterOptions(events, def, active, defs);
  return opts.length ? opts[0] : undefined;
}

// --- Colour ------------------------------------------------------------------------------
// ColorBrewer RdYlGn through its yellow midpoint — a straight red→green RGB blend goes
// muddy grey in the middle.
export const EVENT_COLOR_LOW: readonly [number, number, number] = [215, 48, 39];
export const EVENT_COLOR_MID: readonly [number, number, number] = [254, 224, 139];
export const EVENT_COLOR_HIGH: readonly [number, number, number] = [26, 152, 80];
export const EVENT_COLOR_NEUTRAL = "rgb(130, 130, 130)";

export function lerpEventColor(t: number): string {
  const c = Math.max(0, Math.min(1, t));
  const [from, to, localT] =
    c < 0.5 ? [EVENT_COLOR_LOW, EVENT_COLOR_MID, c / 0.5] : [EVENT_COLOR_MID, EVENT_COLOR_HIGH, (c - 0.5) / 0.5];
  const r = Math.round(from[0] + (to[0] - from[0]) * localT);
  const g = Math.round(from[1] + (to[1] - from[1]) * localT);
  const b = Math.round(from[2] + (to[2] - from[2]) * localT);
  return `rgb(${r}, ${g}, ${b})`;
}

/** Shared by shots and passes so "Left Foot" is one colour on both. */
export const EVENT_BODY_PART_COLORS: Readonly<Record<string, string>> = {
  "Left Foot": "rgb(77, 139, 245)",
  "Right Foot": "rgb(245, 166, 35)",
  Head: "rgb(176, 109, 242)",
  "No Touch": "rgb(150, 150, 150)",
};

/** A Colour-by mode. sortValue (continuous modes only) sets paint order: extremes draw
 *  last, on top of the average majority; a missing value sorts to -Infinity (bottom). */
export interface EventColorMode {
  key: string;
  label: string;
  getColor: (event: ProtoEvent) => string;
  sortValue?: (event: ProtoEvent) => number;
}

export const PASS_COLOR_MODES: readonly EventColorMode[] = [
  { key: "result", label: "Outcome", getColor: (p) => (passCompleted(p) ? "rgb(46, 204, 113)" : "rgb(226, 88, 107)") },
  {
    key: "successProbability",
    label: "Success Probability",
    getColor: (p) => {
      const v = passOf(p).pass_success_probability;
      return typeof v === "number" ? lerpEventColor(v) : EVENT_COLOR_NEUTRAL;
    },
    sortValue: (p) => {
      const v = passOf(p).pass_success_probability;
      return typeof v === "number" ? Math.abs(v - 0.5) : -Infinity;
    },
  },
  {
    key: "obv",
    label: "OBV (Total)",
    // Saturates at ±0.03 (the real p5/p95), not the true extremes — else 90% of passes
    // look identically neutral because of a few outliers.
    getColor: (p) => {
      const v = sbAttrs(p).obv_total_net;
      return typeof v === "number" ? lerpEventColor((v + 0.03) / 0.06) : EVENT_COLOR_NEUTRAL;
    },
    sortValue: (p) => {
      const v = sbAttrs(p).obv_total_net;
      return typeof v === "number" ? Math.abs(v) : -Infinity;
    },
  },
  {
    key: "bodyPart",
    label: "Body Part",
    getColor: (p) => EVENT_BODY_PART_COLORS[passBodyPart(p) ?? ""] || EVENT_COLOR_NEUTRAL,
  },
];

/** The active colour mode for a stored key (unknown/absent → the first mode). */
export function resolveColorMode(modes: readonly EventColorMode[], key: string | null | undefined): EventColorMode {
  return modes.find((m) => m.key === key) || modes[0]!;
}

/** Paint order for a colour mode: ascending sortValue (extremes last); modes without one
 *  keep the data's own (chronological) order. Never mutates the input. */
export function paintOrder<T extends ProtoEvent>(events: T[], mode: EventColorMode): T[] {
  const sv = mode.sortValue;
  return sv ? events.slice().sort((a, b) => sv(a) - sv(b)) : events;
}
